/**
 * useQuickCreate
 *
 * Module-level singleton backing QuickCreateModal — the inline flow for
 * creating a person, organization, or deal. The modal is mounted once in
 * App.vue; any caller imports this composable and invokes open() to trigger
 * it with optional prefill and an onCreated callback.
 *
 * All business logic lives here — Harmonic person enrichment, organization
 * search/match, and the create orchestration (records.create + contacts + org
 * link for deals / employee-list membership for people). QuickCreateModal is
 * a thin form binding over this state.
 *
 * The state is shared module-wide; the actions run through the calling
 * component's `{ client, toast }`.
 */
import { computed, ref, shallowRef } from "vue"
import type { ContactTarget, CreatedRecord, CreateRecordInput, SearchedRecord } from "@auger/core"
import { domainFromEmail } from "@auger/domains"
import { slugify } from "@auger/ui/utils/string"
import type { ComposableDeps } from "@/composables/types"
import { useAssets } from "@/composables/useAssets"
import { useAvatarWrite } from "@/composables/useAvatarWrite"
import { type HarmonicPerson, useHarmonic } from "@/composables/useHarmonic"
import { type OrganizationRef, useOrganizationEnrich } from "@/composables/useOrganizationEnrich"
import { getAuger } from "@/init/auger"
import { getType, getTypeLabel } from "@/stores/types"
import { errorMessage } from "@/utils/errors"

export interface QuickCreateForm {
  type: string
  schemaId: number | null
  name: string
  slug: string
  email: string
  phone: string
  role: string
  linkedin: string
  website: string
  description: string
  organizationId: number | null
  organizationName: string
}

export interface QuickCreateOptions {
  type?: string
  prefill?: Partial<QuickCreateForm>
  onCreated?: (record: CreatedRecord) => void
}

const { enrichPerson } = useHarmonic()
const orgEnrich = useOrganizationEnrich()

// ============================================
// State (module-level — single modal instance)
// ============================================
const show = ref(false)
const options = shallowRef<QuickCreateOptions>({})

const submitting = ref(false)
const enrichingPerson = ref(false)
// Holds the full Harmonic enrichment payload from the most recent successful
// lookup so submit() can persist fields we don't surface as form inputs
// (bio, location, avatar, twitter, facebook, ...). Cleared when the form is
// reset or the email changes to a different enrichment target.
const personEnrichment = ref<ReturnType<typeof harmonicPersonView> | null>(null)

const orgResults = ref<SearchedRecord[]>([])
const orgDropdownOpen = ref(false)

function emptyForm(type = "person"): QuickCreateForm {
  return {
    type,
    schemaId: null,
    name: "",
    slug: "",
    email: "",
    phone: "",
    role: "",
    linkedin: "",
    website: "",
    description: "",
    organizationId: null,
    organizationName: "",
  }
}

const form = ref<QuickCreateForm>(emptyForm())

// Bumped per request so a stale response never lands over a newer one.
let orgSearchSeq = 0
let emailEnrichSeq = 0

// ============================================
// Derived state
// ============================================

// person/organization/deal get the curated "intelligent add" flow (enrichment,
// org matching, per-type fields). Every other type falls back to a plain
// name + slug form.
const isIntelligentType = computed(() =>
  ["person", "organization", "deal"].includes(form.value.type)
)

const typeLabel = computed(() => {
  if (form.value.type === "organization") return "Organization"
  if (form.value.type === "deal") return "Deal"
  if (form.value.type === "person") return "Person"
  return getTypeLabel(form.value.type)
})

const namePlaceholder = computed(() => {
  if (form.value.type === "person") return "Full name"
  if (form.value.type === "deal") return "Deal name"
  if (form.value.type === "organization") return "Organization name"
  return `${typeLabel.value} name`
})

// Preview the auto-derived slug so an empty slug field reads clearly.
const slugPlaceholder = computed(() =>
  form.value.name.trim() ? slugify(form.value.name.trim()) : "auto-generated-from-name"
)

const canSubmit = computed(() => {
  if (!form.value.name.trim()) return false
  if (form.value.type === "person" && !form.value.email.trim()) return false
  if (form.value.type === "deal" && !form.value.organizationId) return false
  return true
})

const trimmedOrgQuery = computed(() => (form.value.organizationName || "").trim())

// Show "Create new" when the user has typed something, hasn't picked a match,
// and no existing org with that exact name is already listed.
const showCreateOrgOption = computed(() => {
  if (!trimmedOrgQuery.value || form.value.organizationId) return false
  const lower = trimmedOrgQuery.value.toLowerCase()
  return !orgResults.value.some((r) => r.name.toLowerCase() === lower)
})

// Flatten Harmonic's raw person payload into the keys this form reads.
function harmonicPersonView(raw: HarmonicPerson | undefined) {
  if (!raw) return null
  const experience = raw.experience ?? []
  const currentRole: NonNullable<HarmonicPerson["experience"]>[number] =
    experience.find((e) => e.is_current_position) || experience[0] || {}
  const phones = raw.contact?.phone_numbers || []
  const socials = raw.socials || {}
  const location =
    raw.location?.address_formatted ||
    [raw.location?.city, raw.location?.state, raw.location?.country].filter(Boolean).join(", ") ||
    null
  return {
    name: raw.full_name || [raw.first_name, raw.last_name].filter(Boolean).join(" ") || null,
    title: currentRole.title || raw.linkedin_headline || null,
    company: currentRole.company_name || null,
    bio: raw.linkedin_headline || null,
    avatar: raw.profile_picture_url || null,
    linkedin: socials.LINKEDIN?.url || null,
    twitter: socials.TWITTER?.url || null,
    facebook: socials.FACEBOOK?.url || null,
    phone: phones[0] || null,
    location,
  }
}

export function useQuickCreate(deps: ComposableDeps) {
  const { client, toast } = deps
  const { uploadUrl } = useAssets({ client, toast: null })
  const { attachAndUpdate } = useAvatarWrite({ client, toast: null })

  // ============================================
  // Type handling
  // ============================================
  function handleTypeChange() {
    // Schemas are scoped to a record type, so swap types = drop the selection.
    form.value.schemaId = null
  }

  // ============================================
  // Organization search / match
  // ============================================
  function runOrgSearch(query: string | null | undefined) {
    const trimmed = (query || "").trim()
    if (trimmed.length < 2) {
      orgResults.value = []
      return
    }

    const seq = ++orgSearchSeq
    getAuger(client)
      .records.search(`%${trimmed}%`, { type: "organization", limit: 10 })
      .then((found) => found)
      .then((records) => {
        if (seq !== orgSearchSeq) return // stale response
        orgResults.value = records
      })
      .catch((err) => {
        if (seq !== orgSearchSeq) return
        console.error("Organization search failed:", err)
        orgResults.value = []
      })
  }

  function handleOrgInput() {
    // Any edit to the text clears the previously-picked org
    if (form.value.organizationId) form.value.organizationId = null
    orgDropdownOpen.value = true
    runOrgSearch(form.value.organizationName)
  }

  function handleOrgFocus() {
    if (trimmedOrgQuery.value.length >= 2 || form.value.organizationId) {
      orgDropdownOpen.value = true
    }
  }

  function handleOrgSelect(org: Pick<OrganizationRef, "id" | "name"> | null | undefined) {
    if (!org) return
    form.value.organizationId = org.id
    form.value.organizationName = org.name
    orgResults.value = []
    orgDropdownOpen.value = false
  }

  function clearOrg() {
    form.value.organizationId = null
    form.value.organizationName = ""
    orgResults.value = []
    orgDropdownOpen.value = false
  }

  function closeOrgDropdown() {
    orgDropdownOpen.value = false
  }

  // Open OrganizationEnrichModal so the user can review Harmonic-enriched
  // data before committing. If we have a work-email domain, seed the
  // enrichment with it; otherwise the enrich modal falls back to manual entry.
  function createAndSelectOrg() {
    if (!trimmedOrgQuery.value) return
    orgDropdownOpen.value = false
    const domain = domainFromEmail(form.value.email || "")
    orgEnrich.open({
      name: trimmedOrgQuery.value,
      domain: domain || "",
      onCreated: handleOrgSelect,
    })
  }

  function onOrgEnter() {
    // Submitting with Enter from the org field: pick exact match, else create, else submit form
    const lower = trimmedOrgQuery.value.toLowerCase()
    const exact = orgResults.value.find((r) => r.name.toLowerCase() === lower)
    if (exact) {
      handleOrgSelect(exact)
      return
    }
    if (showCreateOrgOption.value) {
      createAndSelectOrg()
      return
    }
    submit()
  }

  // ============================================
  // Harmonic person enrichment
  // ============================================

  // On email blur, ask Harmonic to enrich the person. Only fills empty fields
  // so the user's own input is never overwritten. Runs once per email (cached).
  async function handleEmailBlur() {
    if (form.value.type !== "person") return
    const email = (form.value.email || "").trim()
    if (!email || !email.includes("@")) return

    const seq = ++emailEnrichSeq
    enrichingPerson.value = true
    personEnrichment.value = null
    try {
      const result = await enrichPerson({ email })
      if (seq !== emailEnrichSeq) return // email changed while we were fetching
      if (!result.found) return

      const d = harmonicPersonView(result.data)
      if (!d) return
      // Stash the full result so submit() can persist bio/location/avatar/
      // twitter/facebook — fields we don't expose as form inputs.
      personEnrichment.value = d
      if (d.name && !form.value.name.trim()) form.value.name = d.name
      if (d.title && !form.value.role.trim()) form.value.role = d.title
      if (d.linkedin && !form.value.linkedin.trim()) form.value.linkedin = d.linkedin
      if (d.phone && !form.value.phone.trim()) form.value.phone = d.phone
      if (d.company && !form.value.organizationName.trim() && !form.value.organizationId) {
        form.value.organizationName = d.company
        // Kick off a search so an existing Auger org with this name can be
        // auto-matched (user can still click a result to replace).
        runOrgSearch(d.company)
      }
    } catch (err) {
      console.error("Harmonic person enrichment failed:", err)
    } finally {
      if (seq === emailEnrichSeq) enrichingPerson.value = false
    }
  }

  // ============================================
  // Create orchestration
  // ============================================

  /**
   * Create a contact, swallowing errors with a warning toast so a single
   * bad contact doesn't abort the whole flow.
   */
  async function tryCreateContact(recordId: number, contact: ContactTarget, label: string) {
    try {
      const auger = getAuger(client)
      await auger.record(recordId).contacts.add("contacts", contact)
    } catch (err) {
      console.error(`Failed to save ${label}:`, err)
      toast?.warning?.(`${typeLabel.value} created, but failed to save ${label}.`)
    }
  }

  async function submit() {
    if (!canSubmit.value || submitting.value) return

    submitting.value = true
    try {
      const recordType = getType(form.value.type)
      const isPerson = form.value.type === "person"
      const enrichment = isPerson ? personEnrichment.value : null

      // For people, fold the Harmonic-enriched data fields onto record.data
      // so the new record is fully populated on day one (title, bio); the
      // avatar is an image field, uploaded once the record exists.
      // Form inputs win — the user may have edited them post-enrichment —
      // but non-form fields come straight from the enrichment.
      const data: Record<string, unknown> = {}
      if (isPerson) {
        if (form.value.role.trim()) data.role = form.value.role.trim()
        if (enrichment) {
          if (enrichment.title) data.title = enrichment.title
          if (enrichment.bio) data.bio = enrichment.bio
        }
      }

      // Default deals to status="open" so they show up under the
      // pipelines kanban's default status filter. Users can change it
      // later via the Status field on the deal record page.
      if (form.value.type === "deal") {
        data.status = "open"
      }

      const recordPayload: CreateRecordInput = {
        type: form.value.type,
        name: form.value.name.trim(),
        schema_id: form.value.schemaId ?? recordType?.schema_id,
        data,
      }

      // Honour an explicit slug (generic types only); otherwise it's omitted and
      // the DB trigger derives a collision-checked one.
      if (form.value.slug.trim()) {
        recordPayload.slug = form.value.slug.trim()
      }

      if (!isPerson && form.value.description.trim()) {
        recordPayload.description = form.value.description.trim()
      }

      const record = await getAuger(client).records.create(recordPayload)

      // Attach contacts (email/phone/link) — independent, best-effort.
      if (isPerson && form.value.email.trim()) {
        await tryCreateContact(
          record.id,
          { contactType: "email", value: form.value.email.trim(), isPrimary: true },
          "email"
        )
      }
      if (isPerson && form.value.phone.trim()) {
        await tryCreateContact(
          record.id,
          { contactType: "phone", value: form.value.phone.trim() },
          "phone"
        )
      }
      if (!isPerson && form.value.website.trim()) {
        await tryCreateContact(
          record.id,
          {
            contactType: "url",
            value: form.value.website.trim(),
            label: "website",
            isPrimary: true,
          },
          "website"
        )
      }
      if (form.value.linkedin.trim()) {
        await tryCreateContact(
          record.id,
          { contactType: "url", value: form.value.linkedin.trim(), label: "linkedin" },
          "LinkedIn"
        )
      }
      // Social handles from Harmonic that aren't surfaced as form inputs.
      if (isPerson && enrichment?.twitter) {
        await tryCreateContact(
          record.id,
          { contactType: "url", value: enrichment.twitter, label: "twitter" },
          "Twitter"
        )
      }
      if (isPerson && enrichment?.facebook) {
        await tryCreateContact(
          record.id,
          { contactType: "url", value: enrichment.facebook, label: "facebook" },
          "Facebook"
        )
      }
      if (isPerson && enrichment?.location) {
        await tryCreateContact(
          record.id,
          { contactType: "location", value: enrichment.location },
          "location"
        )
      }

      // Deals link to the selected organization (their schemas declare the
      // `organization` field) — required, a dealless-of-org breaks downstream
      // Pipedrive mirror. A person's relationship to an organization is presence
      // in the employee list parented to it, created lazily when the
      // organization has none — best-effort, since it's optional for people.
      const isDeal = form.value.type === "deal"
      const organizationId = form.value.organizationId
      if (isDeal && organizationId && recordType?.schema_id) {
        try {
          await getAuger(client).records.update(
            record.id,
            { organization: { add: [organizationId] } },
            { read: false }
          )
        } catch (err) {
          const error = errorMessage(err)
          console.error("Failed to link organization:", err)
          toast?.error?.(`Failed to link organization: ${error}`)
          return
        }
      }
      if (isPerson && enrichment?.avatar) {
        try {
          const asset = await uploadUrl(enrichment.avatar, {
            record,
            origin: "harmonic_enrichment",
          })
          await attachAndUpdate(asset, record)
        } catch (err) {
          console.error("Failed to set avatar:", err)
        }
      }
      if (isPerson && organizationId) {
        try {
          const auger = getAuger(client)
          const list = await auger.lists.ensureList({
            parentId: organizationId,
            recordType: "person",
            listingRole: "employee",
            name: `${form.value.organizationName.trim()} Employees`,
          })
          await auger.lists.add({ listId: list.id, recordId: record.id })
        } catch (err) {
          console.error("Failed to add to organization employees:", err)
          toast?.warning?.(
            `${typeLabel.value} created, but failed to add to the organization's employees.`
          )
        }
      }

      toast?.success?.(`Created ${typeLabel.value}: ${record.name}`)
      created(record)
    } catch (err) {
      const error = errorMessage(err)
      toast?.error?.(`Failed to create ${typeLabel.value}: ${error}`)
    } finally {
      submitting.value = false
    }
  }

  // ============================================
  // Open / close lifecycle
  // ============================================
  function open(opts: QuickCreateOptions = {}) {
    options.value = opts
    // Apply defaults + caller prefill and clear all transient enrichment/search
    // state so a reopened modal never shows the previous entry's data.
    form.value = { ...emptyForm(opts.type || "person"), ...(opts.prefill || {}) }
    orgResults.value = []
    orgDropdownOpen.value = false
    enrichingPerson.value = false
    personEnrichment.value = null
    submitting.value = false
    show.value = true

    // If the caller prefilled an email (e.g. PersonPopover for a calendar
    // attendee), kick off enrichment immediately instead of waiting for the
    // user to blur the field.
    if (form.value.type === "person" && form.value.email) {
      handleEmailBlur()
    }
  }

  function created(record: CreatedRecord) {
    options.value.onCreated?.(record)
    close()
  }

  function close() {
    show.value = false
    options.value = {}
  }

  return {
    // Modal state
    show,
    form,
    submitting,
    enrichingPerson,

    // Derived form state
    isIntelligentType,
    typeLabel,
    namePlaceholder,
    slugPlaceholder,
    canSubmit,

    // Organization search state
    orgResults,
    orgDropdownOpen,
    trimmedOrgQuery,
    showCreateOrgOption,

    // Form actions
    handleTypeChange,
    handleEmailBlur,

    // Organization actions
    handleOrgInput,
    handleOrgFocus,
    handleOrgSelect,
    clearOrg,
    closeOrgDropdown,
    createAndSelectOrg,
    onOrgEnter,

    // Lifecycle
    open,
    submit,
    created,
    close,
  }
}
